/**
 * Middleware de redux para los datos de uso: modales abiertos y cerrados
 * (solo el nombre del modal y cuánto estuvo abierto), avisos mostrados (tipo
 * y si llevan botón, nunca el texto) y botón «Deshacer»/«Rehacer» pulsado.
 */
import type { Middleware } from '@reduxjs/toolkit';

import {
  closeModal,
  collapseModals,
  popModal,
  replaceModal,
} from '#modals/modalsSlice';
import { addNotification } from '#notifications/notificationsSlice';
import type { Notification } from '#notifications/notificationsSlice';
import { redo, undo } from '#undo';

import { pantallaUso, registrarUso } from './index';

type EstadoModales = { modals?: { modalStack?: Array<{ name: string }> } };

/** Clave estable de un aviso (su id si es una palabra; nunca un uuid). */
function claveAviso(id: unknown): string | undefined {
  if (typeof id !== 'string') {
    return undefined;
  }
  if (/[0-9a-f]{8}-[0-9a-f]{4}/i.test(id)) {
    return undefined;
  }
  return id;
}

function tipoBoton(boton: Notification['button']) {
  if (!boton) {
    return undefined;
  }
  if (boton.action === undo) {
    return 'deshacer';
  }
  if (boton.action === redo) {
    return 'rehacer';
  }
  return 'otro';
}

function pilaModales(estado: unknown): ReadonlyArray<{ name: string }> {
  return (estado as EstadoModales).modals?.modalStack ?? [];
}

const MOTIVO_CIERRE: Record<string, string> = {
  [popModal.type]: 'atras',
  [closeModal.type]: 'cerrar',
  [collapseModals.type]: 'plegar',
  [replaceModal.type]: 'reemplazo',
};

/** `registrar` se puede cambiar en los tests. */
export function crearMiddlewareUso(registrar = registrarUso): Middleware {
  // Cuándo se abrió cada modal de la pila (por identidad del objeto, que no
  // cambia mientras siga en la pila).
  const abiertoDesde = new WeakMap<object, number>();

  return store => next => action => {
    let accion = action;
    try {
      if (addNotification.match(action)) {
        accion = registrarAviso(action, registrar);
      }
    } catch {
      // el registro de uso nunca debe romper la app
    }

    const antes = pilaModales(store.getState());
    const resultado = next(accion);
    try {
      const despues = pilaModales(store.getState());
      if (antes !== despues) {
        const ahora = performance.now();
        const tipo = (action as { type?: string }).type ?? '';
        for (const m of antes) {
          if (!despues.includes(m)) {
            const desde = abiertoDesde.get(m);
            registrar('modal_cerrar', {
              modal: m.name,
              motivo: MOTIVO_CIERRE[tipo] ?? 'otro',
              ms: desde != null ? ahora - desde : undefined,
            });
          }
        }
        for (const m of despues) {
          if (!antes.includes(m)) {
            abiertoDesde.set(m, ahora);
            registrar('modal_abrir', { modal: m.name, n: despues.length });
          }
        }
      }
    } catch {
      // el registro de uso nunca debe romper la app
    }
    return resultado;
  };
}

/** Apunta el aviso y, si tiene botón, lo envuelve para saber si se pulsa. */
function registrarAviso(
  action: ReturnType<typeof addNotification>,
  registrar: typeof registrarUso,
) {
  const n = action.payload.notification;
  const boton = tipoBoton(n.button);
  const sinNombre = !n.origen && !claveAviso(n.id);
  registrar('aviso', {
    tipo: n.type ?? 'message',
    aviso: claveAviso(n.id),
    // Un aviso sin nombre ni origen: al menos en qué pantalla salió.
    origen: n.origen,
    pantalla: sinNombre ? (pantallaUso() ?? undefined) : undefined,
    conBoton: !!n.button,
    boton,
  });
  if (!n.button || !boton) {
    return action;
  }
  const original = n.button.action;
  const aviso = claveAviso(n.id);
  return {
    ...action,
    payload: {
      ...action.payload,
      notification: {
        ...n,
        button: {
          ...n.button,
          action: () => {
            registrar('aviso_boton', { boton, aviso });
            return original();
          },
        },
      },
    },
  };
}
